/** venture/projectFolder.ts — 조합(프로젝트)마다 저장 폴더 하나 (2026-11-01 지도사 「스캔이나 원본 누르면 어느 폴더에 저장할지 최초 1회 물어보고 거기에 · 조합마다 처음에 하나만 · 다른 항목 눌러도 또 묻지 말고」)
 *  · 처음 저장할 때만 폴더를 고르고(Chrome · Edge 폴더 선택), 그 폴더를 브라우저에 기억(IndexedDB · 조합 id 별)
 *  · 다음부터는 그 폴더에 바로 저장 — 같은 이름이면 덮어씀(다시 받으면 최신본으로 바뀜)
 *  · 브라우저를 다시 열면 Chrome 이 「이 폴더 편집 허용」을 한 번 물을 수 있음(폴더를 다시 고르지는 않음 · 「방문할 때마다 허용」을 고르면 다음부턴 안 물음)
 *  · 폴더 선택을 못 쓰는 브라우저(사파리 · 파이어폭스) · 막힌 경우 → 예전처럼 다운로드 폴더로
 *  · 고르기 전 안내 창(guidedPick) — 문서 · 바탕화면 자체를 골라 막히고 취소해 저장이 사라지던 것(2026-11-02)
 *  · 폴더 바꾸기 = changeProjectDir */

interface DirHandle {
  name: string;
  getFileHandle(name: string, opts?: { create?: boolean }): Promise<{ createWritable(): Promise<{ write(d: Blob): Promise<void>; close(): Promise<void> }> }>;
  queryPermission?(o: { mode: 'readwrite' }): Promise<'granted' | 'denied' | 'prompt'>;
  requestPermission?(o: { mode: 'readwrite' }): Promise<'granted' | 'denied' | 'prompt'>;
}
type Picker = (o?: { id?: string; mode?: string; startIn?: string }) => Promise<DirHandle>;

export class SaveCancelled extends Error { constructor() { super('저장을 취소했습니다'); this.name = 'SaveCancelled'; } }

const DB = 'roum-venture'; const STORE = 'folders';
const mem = new Map<string, DirHandle>();
const listeners = new Set<() => void>();

function picker(): Picker | null {
  const f = (window as unknown as { showDirectoryPicker?: Picker }).showDirectoryPicker;
  return typeof f === 'function' ? f : null;
}
export const folderSupported = (): boolean => !!picker();

function idb(): Promise<IDBDatabase | null> {
  return new Promise((res) => {
    try {
      const r = indexedDB.open(DB, 1);
      r.onupgradeneeded = () => { if (!r.result.objectStoreNames.contains(STORE)) r.result.createObjectStore(STORE); };
      r.onsuccess = () => res(r.result);
      r.onerror = () => res(null);
    } catch { res(null); }
  });
}
async function idbGet(pid: string): Promise<DirHandle | null> {
  const db = await idb(); if (!db) return null;
  return new Promise((res) => {
    try { const q = db.transaction(STORE).objectStore(STORE).get(pid); q.onsuccess = () => res((q.result as DirHandle) || null); q.onerror = () => res(null); } catch { res(null); }
  });
}
async function idbPut(pid: string, h: DirHandle): Promise<void> {
  const db = await idb(); if (!db) return;
  await new Promise<void>((res) => {
    try { const t = db.transaction(STORE, 'readwrite'); t.objectStore(STORE).put(h, pid); t.oncomplete = () => res(); t.onerror = () => res(); } catch { res(); }
  });
}

/** 기억해 둔 폴더 이름(화면 표시용) — 없으면 '' */
export async function projectDirName(pid: string): Promise<string> {
  const h = mem.get(pid) || await idbGet(pid);
  if (h) mem.set(pid, h);
  return h ? h.name : '';
}
export function onFolderChange(fn: () => void): () => void { listeners.add(fn); return () => { listeners.delete(fn); }; }

/** 폴더 고르기 안내 창 (2026-11-02 지도사 「이 폴더를 열 수 없음 · 저장 안 됨」)
 *  원인: Chrome 은 문서 · 바탕화면 · 다운로드 폴더 「그 자체」를 고르면 「시스템 파일이 있어 열 수 없음」으로 막고,
 *  거기서 「취소」를 누르면 저장이 통째로 취소됐다(처음 열리는 곳이 「문서」라 그대로 고르기 쉬움).
 *  · 고르기 전에 한 줄 안내(그 안에 조합 폴더를 만들어 고르기) · 브라우저 창은 이 창의 단추를 누를 때 연다(사용자 동작 규칙)
 *  · 브라우저 창에서 취소해도 이 창이 남아 다시 고르거나 「다운로드 폴더에 저장」을 고를 수 있다 — 조용히 저장이 사라지지 않게
 *  돌려주는 값: 고른 폴더 · 'downloads'(다운로드 폴더로) · 닫으면 SaveCancelled */
function guidedPick(id: string, change: boolean): Promise<DirHandle | 'downloads'> {
  const f = picker();
  return new Promise((resolve, reject) => {
    if (!f) { resolve('downloads'); return; }
    const wrap = document.createElement('div');
    wrap.style.cssText = 'position:fixed;inset:0;z-index:2147482000;background:rgba(17,24,39,.42);display:flex;align-items:center;justify-content:center;padding:16px;'
      + 'font-family:Pretendard,"Apple SD Gothic Neo",system-ui,sans-serif';
    const box = document.createElement('div');
    box.setAttribute('role', 'dialog'); box.setAttribute('aria-modal', 'true');
    box.style.cssText = 'background:#fff;border-radius:14px;width:min(440px,100%);padding:22px 22px 18px;box-shadow:0 20px 50px rgba(17,24,39,.25);color:#111827;word-break:keep-all';
    const h = document.createElement('div');
    h.textContent = change ? '이 조합의 저장 폴더 바꾸기' : '이 조합 파일을 저장할 폴더를 골라 주세요';
    h.style.cssText = 'font-size:16px;font-weight:800;margin-bottom:10px';
    const p1 = document.createElement('div');
    p1.style.cssText = 'font-size:13px;line-height:1.65;color:#374151';
    p1.innerHTML = '처음 한 번만 고르면 이 조합의 원본 · 스캔본 · 전체 다운로드가 모두 그 폴더에 저장돼요.'
      + '<div style="margin-top:10px;padding:10px 12px;background:#F3F4F6;border-radius:10px">'
      + '<b>문서 · 바탕화면 · 다운로드 폴더 그 자체</b>는 브라우저가 막아요(「이 폴더를 열 수 없음」).<br>'
      + '그 안에서 <b>「새 폴더」로 조합 이름 폴더를 만들어</b> 그 폴더를 골라 주세요.</div>';
    const note = document.createElement('div');
    note.style.cssText = 'font-size:12.5px;font-weight:600;color:#B45309;margin-top:10px;min-height:0';
    const row = document.createElement('div');
    row.style.cssText = 'display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-top:16px';
    const btn = (label: string, primary: boolean) => {
      const b = document.createElement('button'); b.type = 'button'; b.textContent = label;
      b.style.cssText = 'height:40px;border-radius:9px;font-family:inherit;font-size:13.5px;font-weight:700;cursor:pointer;'
        + (primary ? 'border:0;background:#111827;color:#fff' : 'border:1px solid #D1D5DB;background:#fff;color:#374151');
      return b;
    };
    const dl = btn(change ? '취소' : '다운로드 폴더로', false);
    const go = btn('폴더 고르기', true);
    row.append(dl, go);
    const close = (fn: () => void) => { window.removeEventListener('keydown', onKey); wrap.remove(); fn(); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(() => reject(new SaveCancelled())); };
    window.addEventListener('keydown', onKey);
    wrap.addEventListener('mousedown', (e) => { if (e.target === wrap) close(() => reject(new SaveCancelled())); });
    dl.onclick = () => close(() => (change ? reject(new SaveCancelled()) : resolve('downloads')));
    go.onclick = () => {
      // 이 단추를 누른 그 순간에 브라우저 창을 연다(다른 await 를 앞에 두지 않는다)
      f({ id: 'roum-' + id.replace(/[^a-zA-Z0-9]/g, '').slice(0, 24), mode: 'readwrite', startIn: 'documents' })
        .then((dir) => close(() => resolve(dir)))
        .catch((e: unknown) => {
          if (e && (e as { name?: string }).name === 'AbortError') {
            note.textContent = change ? '폴더를 고르지 않았어요. 다시 골라 주세요.' : '폴더를 고르지 않았어요. 다시 고르거나 「다운로드 폴더로」를 눌러 주세요.';
            return;
          }
          close(() => resolve('downloads'));   // 막힘(보안 정책 등) → 다운로드 폴더로
        });
    };
    box.append(h, p1, note, row);
    wrap.appendChild(box);
    document.body.appendChild(wrap);
    go.focus();
  });
}

/** 조합을 모르는 저장(한 번만 쓰는 폴더) — 안내 창을 거쳐 고르기 · 다운로드 폴더면 null · 닫으면 SaveCancelled */
export async function chooseFolderOnce(): Promise<DirHandle | null> {
  const r = await guidedPick('once', false);
  return r === 'downloads' ? null : r;
}

const toDownloads = new Set<string>();   // 「다운로드 폴더로」를 고른 조합 — 이 화면을 연 동안은 다시 묻지 않는다

async function pick(pid: string, change = false): Promise<DirHandle | null> {
  const r = await guidedPick(pid, change);   // 닫으면 SaveCancelled 가 그대로 올라간다
  if (r === 'downloads') { toDownloads.add(pid); return null; }
  toDownloads.delete(pid);
  mem.set(pid, r); await idbPut(pid, r); listeners.forEach((l) => l());
  return r;
}

/** 이 조합의 저장 폴더 — 처음이면 고르게 하고, 기억한 폴더는 권한만 확인. 단추를 누른 직후(다른 await 전에) 불러야 한다(브라우저 사용자 동작 규칙)
 *  폴더 선택을 못 쓰면 null(다운로드 폴더) · 고르기를 취소하면 SaveCancelled */
export async function projectDir(pid: string): Promise<DirHandle | null> {
  if (!picker()) return null;
  const h = mem.get(pid) || await idbGet(pid);
  if (h) {
    mem.set(pid, h);
    try {
      const q = h.queryPermission ? await h.queryPermission({ mode: 'readwrite' }) : 'granted';
      if (q === 'granted') return h;
      const r = h.requestPermission ? await h.requestPermission({ mode: 'readwrite' }) : 'denied';
      if (r === 'granted') return h;
    } catch { /* 폴더가 지워졌거나 옮겨짐 → 다시 고르기 */ }
  }
  if (toDownloads.has(pid)) return null;
  return pick(pid);
}

/** 폴더 바꾸기 — 새로 고른 폴더를 기억 */
export async function changeProjectDir(pid: string): Promise<string> {
  try { const h = await pick(pid, true); return h ? h.name : ''; } catch (e) { if (e instanceof SaveCancelled) return ''; throw e; }
}

function download(blob: Blob, name: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = name;
  document.body.appendChild(a); a.click(); a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 4000);
}

/** 한 파일 저장 — 폴더 먼저(사용자 동작이 살아 있을 때) → 파일 만들기(getBlob · 생성 · 스캔 등 오래 걸려도 됨) → 폴더에 쓰기(같은 이름 덮어씀)
 *  폴더를 못 쓰면 다운로드 폴더로. 돌려주는 값: 저장한 폴더 이름('' = 다운로드 폴더) · 취소면 null */
export async function saveToProject(pid: string, name0: string | (() => string), getBlob: () => Promise<Blob>): Promise<string | null> {
  let dir: DirHandle | null = null;
  try { dir = await projectDir(pid); } catch (e) { if (e instanceof SaveCancelled) return null; dir = null; }
  const blob = await getBlob();
  const name = typeof name0 === 'function' ? name0() : name0;   // 파일을 만든 뒤에야 이름을 아는 경우(서버가 정하는 ZIP 이름)
  if (dir) {
    try {
      const fh = await dir.getFileHandle(name.replace(/[\\/:*?"<>|]/g, '_'), { create: true });
      const ws = await fh.createWritable(); await ws.write(blob); await ws.close();
      return dir.name;
    } catch { /* 쓰기 실패(권한 · 폴더 없음) → 다운로드로 */ }
  }
  download(blob, name);
  return '';
}

/** 저장 결과 알림 문구 */
export const savedMsg = (folder: string | null, name: string): string =>
  folder === null ? '저장을 취소했습니다' : folder ? `「${folder}」 폴더에 저장 · ${name}` : `다운로드 폴더에 저장 · ${name}`;
